import { Link } from 'react-router-dom';
import { Button } from '@/components/Button';
import { useMe } from '@/lib/auth';
import { useActiveCompanyId } from '@/lib/active-company';
import { useActivity, useMyCompanies } from '@/lib/companies';
import { hasAnyProfileAnswer } from '@/lib/profile-questions';
import {
  useResponseSummary,
  type FollowUp,
  type ModuleResponseSummary,
} from '@/lib/assessments';
import { downloadWithCompanyHeader } from '@/lib/download';
import { cn } from '@/lib/cn';
import { StatTile } from '@/components/StatTile';

const DAY_MS = 24 * 60 * 60 * 1000;

export function Dashboard() {
  const me = useMe();
  const companyId = useActiveCompanyId();
  const summary = useResponseSummary(companyId);
  const activity = useActivity(companyId);
  const companies = useMyCompanies();
  const company = companies.data?.find((c) => c.id === companyId);

  const modules = summary.data?.modules ?? [];
  const stats = aggregateStats(modules);
  const followUps = aggregateFollowUps(modules);

  const handleExportCsv = () => {
    void downloadWithCompanyHeader('/api/Snapshots/export.csv', 'hexdi-items.csv');
  };

  return (
    <div className="mx-auto max-w-content-wide px-5 py-10 md:px-10 lg:px-20">
      <DashboardHeader
        firstName={firstName(me.data?.name ?? me.data?.email ?? '')}
        companyName={company?.name ?? ''}
        onExportCsv={handleExportCsv}
      />

      {company && !hasAnyProfileAnswer(company) ? <ProfilePromptBanner /> : null}

      <section className="mt-8 grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-4">
        <StatTile
          label="Overall Compliance"
          value={`${stats.compliancePct}%`}
          accent="accent"
          progressPct={stats.compliancePct}
          subtext={
            stats.totalItems > 0
              ? `${stats.compliant} of ${stats.totalItems} compliant`
              : undefined
          }
        />
        <StatTile
          label="Items Assessed"
          value={String(stats.assessedItems)}
          subtext={`of ${stats.totalItems} total`}
          accent="warning"
          progressPct={
            stats.totalItems > 0
              ? Math.round((stats.assessedItems / stats.totalItems) * 100)
              : 0
          }
        />
        <StatTile
          label="Open Deficiencies"
          value={String(stats.deficiencies)}
          subtext={
            stats.highPriority > 0
              ? `${stats.highPriority} high priority`
              : 'No critical items'
          }
          accent="danger"
          progressPct={
            stats.totalItems > 0
              ? Math.round((stats.deficiencies / stats.totalItems) * 100)
              : 0
          }
        />
        <StatTile
          label="Follow-Ups Due"
          value={String(followUps.dueCount)}
          subtext={
            followUps.overdueCount > 0
              ? `${followUps.overdueCount} overdue`
              : 'On track'
          }
          accent="warning"
          progressPct={
            followUps.dueCount > 0
              ? Math.round((followUps.overdueCount / followUps.dueCount) * 100)
              : 0
          }
        />
      </section>

      <section className="mt-6 grid grid-cols-1 gap-5 lg:grid-cols-3">
        <div className="lg:col-span-2">
          <ModulesPanel modules={modules} />
        </div>
        <FollowUpsPanel followUps={followUps.list} />
      </section>

      <section className="mt-6">
        <RecentActivityPanel
          items={activity.data ?? []}
          loading={activity.isLoading}
        />
      </section>
    </div>
  );
}

function ProfilePromptBanner() {
  return (
    <div className="mt-6 flex flex-col items-start justify-between gap-4 rounded-lg border border-accent/30 bg-accent/5 px-6 py-5 md:flex-row md:items-center">
      <div className="flex flex-col gap-1">
        <p className="text-body font-semibold text-text-primary">
          Complete your company profile
        </p>
        <p className="text-body-sm text-text-secondary">
          Answer a few questions about your business so we only show the
          assessment items that actually apply to you.
        </p>
      </div>
      <Button asChild size="sm" className="shrink-0">
        <Link to="/settings/company">Complete profile</Link>
      </Button>
    </div>
  );
}

type DashboardHeaderProps = {
  firstName: string;
  companyName: string;
  onExportCsv: () => void;
};

function DashboardHeader({
  firstName,
  companyName,
  onExportCsv,
}: DashboardHeaderProps) {
  const greeting = greetingForHour(new Date().getHours());
  return (
    <div className="flex flex-col items-start justify-between gap-4 md:flex-row md:items-end">
      <div>
        <h1
          className="font-display font-bold text-text-primary"
          style={{ fontSize: '32px', lineHeight: 1.15, letterSpacing: '-0.02em' }}
        >
          {greeting}
          {firstName ? `, ${firstName}` : ''}
        </h1>
        <p className="mt-2 text-[15px] text-text-secondary">
          {companyName
            ? `Here's your compliance overview for ${companyName}.`
            : "Here's your compliance overview."}
        </p>
      </div>
      <div className="flex items-center gap-3">
        <Button variant="secondary" size="sm" onClick={onExportCsv}>
          Export CSV
        </Button>
        <Button asChild variant="dark" size="sm">
          <Link to="/report">View Full Report</Link>
        </Button>
      </div>
    </div>
  );
}

function ModulesPanel({ modules }: { modules: ModuleResponseSummary[] }) {
  const sorted = [...modules].sort((a, b) => a.displayOrder - b.displayOrder);
  return (
    <div className="overflow-hidden rounded-lg border border-border bg-surface">
      <PanelHeader title="Assessment Modules" link={null} />
      {sorted.length === 0 ? (
        <div className="px-7 py-10 text-body-sm text-text-secondary">
          No assessment modules yet. Complete your company profile to get started.
        </div>
      ) : (
        <ul>
          {sorted.map((m, idx) => (
            <ModuleRow key={m.id} module={m} index={idx + 1} />
          ))}
        </ul>
      )}
    </div>
  );
}

function ModuleRow({
  module: m,
  index,
}: {
  module: ModuleResponseSummary;
  index: number;
}) {
  const total = m.summary.itemResponses.total;
  const compliant = m.summary.itemResponses.meetsStandardCount;
  const nonCompliant = m.summary.itemResponses.warnings.reduce(
    (acc, w) => acc + w.count,
    0,
  );
  const assessed = compliant + nonCompliant;
  const pct = total > 0 ? Math.round((compliant / total) * 100) : 0;
  const tone = pct >= 70 ? 'accent' : pct >= 40 ? 'warning' : 'danger';
  const toneClasses = {
    accent: { bar: 'bg-accent', text: 'text-accent' },
    warning: { bar: 'bg-warning', text: 'text-warning' },
    danger: { bar: 'bg-danger', text: 'text-danger' },
  } as const;
  const t = toneClasses[tone];
  const padded = String(index).padStart(2, '0');
  return (
    <li>
      <Link
        to={`/module/${m.id}`}
        className="flex items-center gap-5 border-b border-border px-7 py-5 transition-colors last:border-b-0 hover:bg-background"
      >
        <span className="flex size-10 shrink-0 items-center justify-center rounded-md bg-text-primary text-body-sm font-bold text-white">
          {padded}
        </span>
        <span className="flex min-w-0 grow flex-col gap-1">
          <span className="text-body-sm font-semibold text-text-primary">
            {m.name}
          </span>
          <span className="text-caption text-text-secondary">
            {total} item{total === 1 ? '' : 's'}
            {assessed > 0 ? ` · ${assessed} assessed` : ''}
          </span>
        </span>
        <span className="flex w-40 shrink-0 flex-col items-end gap-1.5">
          <span className={cn('text-[13px] font-semibold', t.text)}>{pct}%</span>
          <span className="h-1 w-full overflow-hidden rounded-sm bg-surface-tint">
            <span
              className={cn('block h-full', t.bar)}
              style={{ width: `${pct}%` }}
            />
          </span>
        </span>
      </Link>
    </li>
  );
}

function FollowUpsPanel({ followUps }: { followUps: DashboardFollowUp[] }) {
  return (
    <div className="overflow-hidden rounded-lg border border-border bg-surface">
      <PanelHeader title="Follow-Ups" link={null} />
      {followUps.length === 0 ? (
        <div className="px-7 py-10 text-body-sm text-text-secondary">
          No open follow-ups. You're all caught up.
        </div>
      ) : (
        <ul>
          {followUps.slice(0, 5).map((fu) => (
            <FollowUpRow key={fu.id} followUp={fu} />
          ))}
        </ul>
      )}
    </div>
  );
}

function FollowUpRow({ followUp }: { followUp: DashboardFollowUp }) {
  const { tone, label } = statusBadgeForFollowUp(followUp);
  const moduleLabel = followUp.moduleCode
    ? `Module ${followUp.moduleCode}`
    : 'Module —';
  const dueLabel = followUp.dueDate
    ? `Due ${formatDueDate(followUp.dueDate)}`
    : 'No due date';
  return (
    <li className="flex flex-col gap-2 border-b border-border px-7 py-5 last:border-b-0">
      <div className="flex items-start justify-between gap-3">
        <p className="text-[13px] font-semibold text-text-primary">
          {followUp.note || 'Untitled follow-up'}
        </p>
        <StatusPill tone={tone}>{label}</StatusPill>
      </div>
      <p className="text-caption text-text-secondary">
        {moduleLabel} · {dueLabel}
      </p>
    </li>
  );
}

function StatusPill({
  tone,
  children,
}: {
  tone: 'danger' | 'warning' | 'neutral';
  children: React.ReactNode;
}) {
  const styles = {
    danger: 'bg-danger text-white',
    warning: 'bg-warning text-white',
    neutral: 'bg-surface-tint text-text-secondary',
  } as const;
  return (
    <span
      className={cn(
        'inline-flex items-center rounded px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.04em]',
        styles[tone],
      )}
    >
      {children}
    </span>
  );
}

type ActivityRow = {
  id: string;
  action: string;
  createdAt: string;
  user?: { name: string | null; email: string } | null;
};

function RecentActivityPanel({
  items,
  loading,
}: {
  items: ActivityRow[];
  loading: boolean;
}) {
  return (
    <div className="overflow-hidden rounded-lg border border-border bg-surface">
      <PanelHeader title="Recent Activity" subtitle="Last 7 days" link={null} />
      {loading ? (
        <div className="px-7 py-6 text-body-sm text-text-secondary">Loading…</div>
      ) : items.length === 0 ? (
        <div className="px-7 py-10 text-body-sm text-text-secondary">
          No activity yet. Changes you and your team make will show up here.
        </div>
      ) : (
        <ul>
          {items.slice(0, 8).map((a) => (
            <ActivityListRow key={a.id} item={a} />
          ))}
        </ul>
      )}
    </div>
  );
}

function ActivityListRow({ item }: { item: ActivityRow }) {
  const { dotClass, sentence } = describeActivity(item);
  const author = item.user?.name ?? item.user?.email ?? 'System';
  return (
    <li className="flex items-center gap-4 border-b border-border px-7 py-4 last:border-b-0">
      <span className={cn('size-2 shrink-0 rounded-full', dotClass)} />
      <p className="grow text-body-sm text-text-primary">
        <span className="font-medium">{author}</span>{' '}
        <span className="text-text-secondary">{sentence}</span>
      </p>
      <p className="shrink-0 text-caption text-text-secondary">
        {timeAgo(item.createdAt)}
      </p>
    </li>
  );
}

function PanelHeader({
  title,
  subtitle,
  link,
}: {
  title: string;
  subtitle?: string;
  link?: { label: string; to: string } | null;
}) {
  return (
    <div className="flex items-center justify-between border-b border-border px-7 py-5">
      <div className="flex items-baseline gap-3">
        <h2 className="text-body font-semibold text-text-primary">{title}</h2>
        {subtitle ? (
          <span className="text-caption text-text-secondary">{subtitle}</span>
        ) : null}
      </div>
      {link ? (
        <Link
          to={link.to}
          className="text-body-sm font-medium text-accent hover:underline"
        >
          {link.label}
        </Link>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

type DashboardFollowUp = FollowUp & { moduleCode: string };

function aggregateStats(modules: ModuleResponseSummary[]) {
  let totalItems = 0;
  let compliant = 0;
  let nonCompliant = 0;
  let highPriority = 0;
  for (const m of modules) {
    totalItems += m.summary.itemResponses.total;
    compliant += m.summary.itemResponses.meetsStandardCount;
    for (const w of m.summary.itemResponses.warnings) nonCompliant += w.count;
    for (const fu of m.summary.followUpItems) {
      if (fu.severity === 'CRITICAL' || fu.severity === 'HIGH') highPriority += 1;
    }
  }
  const compliancePct = totalItems > 0 ? Math.round((compliant / totalItems) * 100) : 0;
  return {
    totalItems,
    compliant,
    deficiencies: nonCompliant,
    assessedItems: compliant + nonCompliant,
    compliancePct,
    highPriority,
  };
}

function aggregateFollowUps(modules: ModuleResponseSummary[]): {
  list: DashboardFollowUp[];
  dueCount: number;
  overdueCount: number;
} {
  const all: DashboardFollowUp[] = [];
  for (const m of modules) {
    for (const fu of m.summary.followUpItems) {
      if (fu.status === 'RESOLVED' || fu.status === 'VERIFIED') continue;
      all.push({ ...fu, moduleCode: m.code });
    }
  }
  all.sort((a, b) => {
    const aDate = a.dueDate ? new Date(a.dueDate).getTime() : Infinity;
    const bDate = b.dueDate ? new Date(b.dueDate).getTime() : Infinity;
    return aDate - bDate;
  });
  const now = Date.now();
  const overdueCount = all.filter(
    (fu) => fu.dueDate && new Date(fu.dueDate).getTime() < now,
  ).length;
  return { list: all, dueCount: all.length, overdueCount };
}

function statusBadgeForFollowUp(fu: DashboardFollowUp): {
  tone: 'danger' | 'warning' | 'neutral';
  label: string;
} {
  if (fu.dueDate) {
    const due = new Date(fu.dueDate).getTime();
    const now = Date.now();
    if (due < now) return { tone: 'danger', label: 'Overdue' };
    if (due - now < 7 * DAY_MS) return { tone: 'warning', label: 'Due Soon' };
  }
  return { tone: 'neutral', label: 'Open' };
}

function describeActivity(item: ActivityRow): {
  dotClass: string;
  sentence: string;
} {
  const map: Record<
    string,
    { dotClass: string; sentence: string }
  > = {
    RESPONSE_CHANGED: {
      dotClass: 'bg-success',
      sentence: 'recorded an item response',
    },
    FOLLOWUP_CREATED: {
      dotClass: 'bg-warning',
      sentence: 'created a follow-up',
    },
    FOLLOWUP_UPDATED: {
      dotClass: 'bg-warning',
      sentence: 'updated a follow-up',
    },
    FOLLOWUP_RESOLVED: {
      dotClass: 'bg-success',
      sentence: 'resolved a follow-up',
    },
    FOLLOWUP_VERIFIED: {
      dotClass: 'bg-success',
      sentence: 'verified a follow-up',
    },
    PROFILE_CHANGED: {
      dotClass: 'bg-accent',
      sentence: 'updated the company profile',
    },
    REPORT_GENERATED: {
      dotClass: 'bg-accent',
      sentence: 'generated a report',
    },
    SUBSCRIPTION_CHANGED: {
      dotClass: 'bg-accent',
      sentence: 'changed the subscription',
    },
    USER_LOGIN: { dotClass: 'bg-text-secondary', sentence: 'signed in' },
    USER_LOGOUT: { dotClass: 'bg-text-secondary', sentence: 'signed out' },
    ROLE_CHANGED: {
      dotClass: 'bg-accent',
      sentence: 'updated team roles',
    },
  };
  return (
    map[item.action] ?? {
      dotClass: 'bg-text-secondary',
      sentence: item.action.toLowerCase().replaceAll('_', ' '),
    }
  );
}

function greetingForHour(hour: number): string {
  if (hour < 12) return 'Good morning';
  if (hour < 17) return 'Good afternoon';
  return 'Good evening';
}

function firstName(displayName: string): string {
  if (!displayName) return '';
  const trimmed = displayName.trim();
  if (trimmed.includes('@')) return '';
  return trimmed.split(/\s+/)[0] ?? '';
}

function formatDueDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

function timeAgo(iso: string): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const now = Date.now();
  const diff = Math.max(0, now - then);
  const minutes = Math.floor(diff / 60_000);
  if (minutes < 1) return 'Just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days === 1) return 'Yesterday';
  if (days < 7) return `${days}d ago`;
  return new Date(iso).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
  });
}
